import { jwtDecode } from "jwt-decode";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { api } from "../lib/api";

export interface IUser {
	id: string;
	name: string;
	email: string;
	insertComments: boolean;
}

export interface IUserProfile {
	id: number;
	userId: string;
	username: string;
	email: string;
	jti: string;
	token_type: string;
	iat: number;
	exp: number;
}

export interface IUserContext {
	user: IUser | null;
	isLoggedIn: boolean;
	isLoading: boolean;
	insertComments: boolean;
	setInsertComments: React.Dispatch<React.SetStateAction<boolean>>;
	fetchUser: () => Promise<IUser | null>;
	logout: () => void;
}

const UserContext = createContext<IUserContext | null>(null);

async function getUserProfile(): Promise<IUserProfile | null> {
	const token = localStorage.getItem("authToken");
	if (!token) {
		return null;
	}

	const response: any = await api.get("api/v1/user/verify");
	if (!response.success) {
		localStorage.removeItem("authToken");
		throw new Error(response.message ?? "Failed to verify the user token");
	}

	const user: IUserProfile = jwtDecode(token);
	return user;
}

export function UserProvider({ children }: { children: React.ReactNode }) {
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [user, setUser] = useState<IUser | null>(null);
	const [insertComments, setInsertComments] = useState(true);

	const isLoggedIn = !!user;

	const fetchUser = useCallback(async () => {
		setIsLoading(true);
		let user = null;
		const userProfile = await getUserProfile();

		if (userProfile) {
			user = {
				id: userProfile.userId,
				email: userProfile.email,
				name: userProfile.username,
				insertComments,
			};
		}

		setUser(user);
		setIsLoading(false);
		return user;
	}, []);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	const logout = useCallback(() => {
		setUser(null);
		localStorage.removeItem("authToken");
	}, []);

	return (
		<UserContext.Provider value={{ user, isLoggedIn, fetchUser, logout, isLoading, insertComments, setInsertComments }}>
			{children}
		</UserContext.Provider>
	);
}

export function useUser(): IUserContext {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error("useUser hook must be used within a UserProvider");
	}
	return context;
}
